(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-shop-by-need]';
  const TRACK_SELECTOR = '.needGrid';
  const CARD_SELECTOR = '.needCard';
  const PREVIOUS_BUTTON_SELECTOR = '.needSlider__button--previous';
  const NEXT_BUTTON_SELECTOR = '.needSlider__button--next';

  const MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 767px)');

  const DESKTOP_VISIBLE_CARDS = 4;
  const DRAG_THRESHOLD = 6;
  const END_TOLERANCE = 3;

  /**
   * 初始化单个 Shop by Need Section
   */
  function initShopByNeed(section) {
    if (!section || !section.matches(SECTION_SELECTOR)) return;

    const slider = section.querySelector('.needSlider');
    const track = section.querySelector(TRACK_SELECTOR);

    if (!slider || !track) return;

    /*
     * 防止同一个 Section 重复初始化。
     */
    if (track.dataset.sliderInitialized === 'true') return;

    track.dataset.sliderInitialized = 'true';

    const previousButton = section.querySelector(
      PREVIOUS_BUTTON_SELECTOR
    );

    const nextButton = section.querySelector(
      NEXT_BUTTON_SELECTOR
    );

    const cards = Array.from(
      track.querySelectorAll(CARD_SELECTOR)
    );

    let isDragging = false;
    let hasDragged = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId = null;
    let resizeTimer = null;

    /*
     * AbortController 用于 Shopify 编辑器卸载 Section 时，
     * 自动清除当前 Section 注册的事件。
     */
    const eventController = new AbortController();
    const eventSignal = eventController.signal;

    let resizeObserver = null;

    track.shopByNeedCleanup = cleanup;

    function isMobile() {
      return MOBILE_MEDIA_QUERY.matches;
    }

    /**
     * 取得每次点击左右按钮时需要滚动的距离。
     * PC 和平板每次滚动一屏，即 4 张卡片。
     */
    function getScrollAmount() {
      const firstCard = track.querySelector(CARD_SELECTOR);

      if (!firstCard) {
        return track.clientWidth;
      }

      const trackStyles = window.getComputedStyle(track);

      const gap =
        parseFloat(
          trackStyles.columnGap || trackStyles.gap
        ) || 0;

      const cardWidth =
        firstCard.getBoundingClientRect().width;

      return (cardWidth + gap) * DESKTOP_VISIBLE_CARDS;
    }

    /**
     * 更新左右按钮显示状态与禁用状态。
     */
    function updateNavigation() {
      const maxScrollLeft = Math.max(
        0,
        track.scrollWidth - track.clientWidth
      );

      const hasDesktopOverflow =
        cards.length > DESKTOP_VISIBLE_CARDS &&
        maxScrollLeft > END_TOLERANCE;

      if (!isMobile() && hasDesktopOverflow) {
        slider.classList.add('needSlider--has-navigation');
      } else {
        slider.classList.remove('needSlider--has-navigation');
      }

      if (!previousButton || !nextButton) return;

      const isAtStart =
        track.scrollLeft <= END_TOLERANCE;

      const isAtEnd =
        track.scrollLeft >=
        maxScrollLeft - END_TOLERANCE;

      previousButton.disabled =
        isMobile() || !hasDesktopOverflow || isAtStart;

      nextButton.disabled =
        isMobile() || !hasDesktopOverflow || isAtEnd;

      previousButton.setAttribute(
        'aria-disabled',
        String(previousButton.disabled)
      );

      nextButton.setAttribute(
        'aria-disabled',
        String(nextButton.disabled)
      );
    }

    /**
     * 点击箭头滚动。
     */
    function scrollTrack(direction) {
      if (isMobile()) return;

      track.scrollBy({
        left: getScrollAmount() * direction,
        behavior: 'smooth'
      });
    }

    /**
     * 鼠标开始拖动。
     * 手机端继续使用浏览器原生触摸滑动。
     */
    function handlePointerDown(event) {
      if (
        event.pointerType !== 'mouse' ||
        event.button !== 0
      ) {
        return;
      }

      if (
        track.scrollWidth <=
        track.clientWidth + END_TOLERANCE
      ) {
        return;
      }

      isDragging = true;
      hasDragged = false;
      activePointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;

      track.classList.add('is-dragging');
    }

    /**
     * 鼠标拖动过程中更新滚动位置。
     */
    function handlePointerMove(event) {
      if (
        !isDragging ||
        event.pointerId !== activePointerId
      ) {
        return;
      }

      const distance = event.clientX - startX;

      if (
        !hasDragged &&
        Math.abs(distance) > DRAG_THRESHOLD
      ) {
        hasDragged = true;

        if (!track.hasPointerCapture(event.pointerId)) {
          track.setPointerCapture(event.pointerId);
        }
      }

      if (!hasDragged) return;

      event.preventDefault();

      track.scrollLeft =
        startScrollLeft - distance;
    }

    /**
     * 停止鼠标拖动。
     */
    function stopDragging() {
      if (!isDragging) return;

      if (
        activePointerId !== null &&
        track.hasPointerCapture(activePointerId)
      ) {
        track.releasePointerCapture(activePointerId);
      }

      isDragging = false;
      activePointerId = null;

      track.classList.remove('is-dragging');

      updateNavigation();

      window.setTimeout(() => {
        hasDragged = false;
      }, 0);
    }

    /**
     * 拖动卡片后阻止误跳转。
     */
    function preventClickAfterDrag(event) {
      if (!hasDragged) return;

      event.preventDefault();
      event.stopPropagation();
    }

    /**
     * 防止浏览器默认拖动卡片图片。
     */
    function preventNativeDrag(event) {
      event.preventDefault();
    }

    /**
     * 浏览器尺寸变化后重新计算。
     */
    function handleResize() {
      window.clearTimeout(resizeTimer);

      resizeTimer = window.setTimeout(() => {
        updateNavigation();
      }, 100);
    }

    /**
     * 销毁当前 Section 的事件和观察器。
     */
    function cleanup() {
      window.clearTimeout(resizeTimer);

      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }

      eventController.abort();

      track.classList.remove('is-dragging');
      track.removeAttribute('data-slider-initialized');

      delete track.shopByNeedCleanup;
    }

    if (previousButton) {
      previousButton.addEventListener(
        'click',
        () => scrollTrack(-1),
        { signal: eventSignal }
      );
    }

    if (nextButton) {
      nextButton.addEventListener(
        'click',
        () => scrollTrack(1),
        { signal: eventSignal }
      );
    }

    track.addEventListener(
      'pointerdown',
      handlePointerDown,
      { signal: eventSignal }
    );

    track.addEventListener(
      'pointermove',
      handlePointerMove,
      { signal: eventSignal }
    );

    track.addEventListener(
      'pointerup',
      stopDragging,
      { signal: eventSignal }
    );

    track.addEventListener(
      'pointercancel',
      stopDragging,
      { signal: eventSignal }
    );

    track.addEventListener(
      'lostpointercapture',
      stopDragging,
      { signal: eventSignal }
    );

    track.addEventListener(
      'click',
      preventClickAfterDrag,
      {
        capture: true,
        signal: eventSignal
      }
    );

    track.addEventListener(
      'dragstart',
      preventNativeDrag,
      { signal: eventSignal }
    );

    track.addEventListener(
      'scroll',
      updateNavigation,
      {
        passive: true,
        signal: eventSignal
      }
    );

    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
        signal: eventSignal
      }
    );

    /*
     * 浏览器从手机宽度切换到平板或 PC 宽度时更新。
     */
    MOBILE_MEDIA_QUERY.addEventListener(
      'change',
      updateNavigation,
      { signal: eventSignal }
    );

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        updateNavigation();
      });

      resizeObserver.observe(track);
    }

    /*
     * 图片加载完成后，卡片尺寸可能发生变化。
     */
    const images = track.querySelectorAll('img');

    images.forEach((image) => {
      if (!image.complete) {
        image.addEventListener(
          'load',
          updateNavigation,
          {
            once: true,
            signal: eventSignal
          }
        );
      }
    });

    updateNavigation();
  }

  /**
   * 初始化页面中的全部 Shop by Need Section。
   */
  function initAllShopByNeed(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initShopByNeed(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initShopByNeed);
  }

  /**
   * 页面首次加载。
   */
  function handleDocumentReady() {
    initAllShopByNeed(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      handleDocumentReady,
      { once: true }
    );
  } else {
    handleDocumentReady();
  }

  /**
   * Shopify 主题编辑器重新加载 Section。
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllShopByNeed(event.target);
    }
  );

  /**
   * Shopify 主题编辑器卸载 Section。
   */
  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const section = event.target.matches?.(SECTION_SELECTOR)
        ? event.target
        : event.target.querySelector?.(SECTION_SELECTOR);

      const track = section?.querySelector(TRACK_SELECTOR);

      if (
        track &&
        typeof track.shopByNeedCleanup === 'function'
      ) {
        track.shopByNeedCleanup();
      }
    }
  );
})();